import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { DateTime } from 'luxon';

interface WeekDay {
  day: string;
  date: number;
  count: number;
  isSelected: boolean;
  isCurrentDay: boolean;
}

@Component({
  selector: 'app-week-picker',
  templateUrl: './week-picker.component.html',
  styleUrls: ['./week-picker.component.scss']
})
export class WeekPickerComponent implements OnInit, OnChanges {
  @Input() title: string = 'Upcoming';
  @Input() description: string = 'Follow your weekly visits.';
  @Input() customClass: string = '';
  @Input() selectedDate: DateTime = DateTime.now();
  @Input() visitData: WeekDay[] = [];
  
  @Output() dateSelected = new EventEmitter<DateTime>();
  @Output() weekChanged = new EventEmitter<DateTime>();
  @Output() calendarStateChanged = new EventEmitter<boolean>();
  @Output() dayCountSelected = new EventEmitter<number>();

  weekDays: WeekDay[] = [];
  currentWeek: DateTime = DateTime.now();
  showCalendar: boolean = false;
  calendarDate: Date = new Date();

  ngOnInit() {
    this.currentWeek = DateTime.now();
    this.selectedDate = DateTime.now();
    this.generateWeekDays();
    this.autoSelectToday();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.visitData.length > 0) {
      this.weekDays = this.visitData;
      // Do not call autoSelectToday() when data comes from parent - parent already set isSelected.
      // Calling it would emit dateSelected and cause the parent to refetch in a loop.
    } else {
      this.generateWeekDays();
      this.autoSelectToday();
    }
  }

  private autoSelectToday() {
    const todayIndex = this.weekDays.findIndex(day => day.isCurrentDay);
    if (todayIndex !== -1) {
      this.selectDate(this.weekDays[todayIndex], todayIndex);
    }
  }

  generateWeekDays() {
    const startOfWeek = this.currentWeek.startOf('week').minus({ days: 1 });
    this.weekDays = [];
    
    const sampleCounts = [0, 26, 15, 37, 32, 26, 5];
    
    for (let i = 0; i < 7; i++) {
      const date = startOfWeek.plus({ days: i });
      const isCurrentDay = date.hasSame(DateTime.now(), 'day');
      const isSelected = date.hasSame(this.selectedDate, 'day');
      
      this.weekDays.push({
        day: date.toFormat('ccc'),
        date: date.day,
        count: sampleCounts[i] || Math.floor(Math.random() * 40),
        isSelected,
        isCurrentDay
      });
    }
  }

  selectDate(day: WeekDay, index: number) {
    this.weekDays.forEach((d, i) => {
      d.isSelected = i === index;
    });
    
    const selectedDate = this.currentWeek.startOf('week').minus({ days: 1 }).plus({ days: index });
    this.selectedDate = selectedDate;
    this.dateSelected.emit(selectedDate);
    
    // Emit the selected day's count
    this.dayCountSelected.emit(day.count);
  }

  previousWeek() {
    this.currentWeek = this.currentWeek.minus({ weeks: 1 });
    if (this.visitData?.length > 0) {
      this.weekChanged.emit(this.currentWeek);
      return;
    }
    this.generateWeekDays();
    this.weekChanged.emit(this.currentWeek);
  }

  nextWeek() {
    this.currentWeek = this.currentWeek.plus({ weeks: 1 });
    if (this.visitData?.length > 0) {
      this.weekChanged.emit(this.currentWeek);
      return;
    }
    this.generateWeekDays();
    this.weekChanged.emit(this.currentWeek);
  }

  getCurrentMonthYear(): string {
    return this.currentWeek.toFormat('MMMM yyyy');
  }

  getDateSuffix(date: number): string {
    if (date >= 11 && date <= 13) {
      return 'th';
    }
    switch (date % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  toggleCalendar() {
    this.showCalendar = !this.showCalendar;
    if (this.showCalendar) {
      this.calendarDate = this.currentWeek.toJSDate();
    }
    this.calendarStateChanged.emit(this.showCalendar);
  }

  onCalendarDateSelect(event: any) {
    const selectedDate = DateTime.fromJSDate(event);
    this.currentWeek = selectedDate;
    this.selectedDate = selectedDate;
    this.generateWeekDays();
    this.showCalendar = false;
    // Emit only dateSelected so the parent makes one API call with the clicked date.
    // Do not emit weekChanged here — parent will derive the week from the selected date.
    this.dateSelected.emit(this.selectedDate);
    this.calendarStateChanged.emit(false);
  }

  onCalendarClose() {
    this.showCalendar = false;
  }

  onCalendarPrevMonth() {
    this.currentWeek = this.currentWeek.minus({ months: 1 });
    this.calendarDate = this.currentWeek.toJSDate();
  }

  onCalendarNextMonth() {
    this.currentWeek = this.currentWeek.plus({ months: 1 });
    this.calendarDate = this.currentWeek.toJSDate();
  }

  goToToday() {
    // Close calendar and show week view
    this.showCalendar = false;
    
    // Navigate to today's week
    this.currentWeek = DateTime.now();
    this.selectedDate = DateTime.now();
    this.generateWeekDays();
    
    // Highlight today in the week view
    this.autoSelectToday();
    
    // Emit events
    this.weekChanged.emit(this.currentWeek);
    this.dateSelected.emit(this.selectedDate);
    this.calendarStateChanged.emit(false);
  }
}
